/**
 * Small formatting helpers used across the workspace.
 */

/** Convert minutes to "X.XX h" string. */
export function formatHours(minutes) {
  const m = Number(minutes) || 0;
  return `${(m / 60).toFixed(2)} h`;
}

/** Convert minutes to a number (always 2 decimals). */
export function hoursDecimal(minutes) {
  const m = Number(minutes) || 0;
  return Number((m / 60).toFixed(2));
}

/** Friendly "2h 30m" — used when we want a more granular display. */
export function formatHoursMinutes(minutes) {
  const m = Math.max(0, Number(minutes) || 0);
  const h = Math.floor(m / 60);
  const rem = m - h * 60;
  if (h === 0) return `${rem}m`;
  if (rem === 0) return `${h}h`;
  return `${h}h ${rem}m`;
}

/**
 * The backend stores ISO-ish datetimes WITHOUT a 'Z' (because SQLite's
 * datetime('now') returns local-naive). We've been treating them as UTC
 * by appending 'Z' before parsing — keep doing that here.
 */
export function parseServerDate(s) {
  if (!s) return null;
  // Already has tz info? trust it.
  const d = /[zZ]|[+-]\d\d:?\d\d$/.test(s) ? new Date(s) : new Date(s + 'Z');
  // Guard against Invalid Date (e.g. malformed string from DB).
  return isNaN(d.getTime()) ? null : d;
}

const IN_LOCALE = 'en-IN';
const IN_TZ = { timeZone: 'Asia/Kolkata' };

// Rupee amount with long figures shortened to Indian lakh/crore (₹2 L, ₹1.2 Cr);
// smaller amounts in full (₹40,000). Returns null for empty/zero/invalid input.
export function formatRupeesShort(n) {
  const num = Number(n);
  if (!num || !Number.isFinite(num)) return null;
  const abs = Math.abs(num);
  if (abs >= 1e7) return `₹${(num / 1e7).toLocaleString(IN_LOCALE, { maximumFractionDigits: 2 })} Cr`;
  if (abs >= 1e5) return `₹${(num / 1e5).toLocaleString(IN_LOCALE, { maximumFractionDigits: 2 })} L`;
  return num.toLocaleString(IN_LOCALE, { style: 'currency', currency: 'INR', maximumFractionDigits: 0 });
}

// The exact rupee amount with Indian grouping (₹1,26,90,000) — the hover text
// behind a formatRupeesShort() value. Returns undefined when there's nothing to show.
export function formatRupeesFull(n) {
  const num = Number(n);
  if (!num || !Number.isFinite(num)) return undefined;
  return num.toLocaleString(IN_LOCALE, { style: 'currency', currency: 'INR', minimumFractionDigits: 0, maximumFractionDigits: 2 });
}

export function formatDate(s) {
  const d = parseServerDate(s);
  return d ? d.toLocaleDateString(IN_LOCALE, { day: '2-digit', month: 'short', year: 'numeric', ...IN_TZ }) : '';
}

export function formatDateTime(s) {
  const d = parseServerDate(s);
  return d ? d.toLocaleString(IN_LOCALE, { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', ...IN_TZ }) : '';
}

/** "5 mins ago" / "2 hours ago" / etc. */
export function formatRelative(s) {
  const d = parseServerDate(s);
  if (!d) return '';
  const seconds = Math.max(0, (Date.now() - d.getTime()) / 1000);
  if (seconds < 60)        return 'just now';
  if (seconds < 3600)      return `${Math.floor(seconds / 60)} min ago`;
  if (seconds < 86400)     return `${Math.floor(seconds / 3600)} h ago`;
  if (seconds < 86400 * 7) return `${Math.floor(seconds / 86400)} d ago`;
  return d.toLocaleDateString();
}

/**
 * Formats an entry timestamp as "8 May at 3:45 PM".
 * Today's entries show only "Today at 3:45 PM".
 * Yesterday's entries show "Yesterday at 3:45 PM".
 * Tooltip-friendly full version via formatDateTime().
 */
export function formatExactEntry(s) {
  const d = parseServerDate(s);
  if (!d) return '';
  const now   = new Date();
  const time  = d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yest  = new Date(today.getTime() - 86400000);
  const entry = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  if (entry.getTime() === today.getTime()) return `Today at ${time}`;
  if (entry.getTime() === yest.getTime())  return `Yesterday at ${time}`;
  const day   = d.getDate();
  const month = d.toLocaleString([], { month: 'short' });
  const year  = d.getFullYear() !== now.getFullYear() ? ` ${d.getFullYear()}` : '';
  return `${day} ${month}${year} at ${time}`;
}

/**
 * Returns the best available display name for a platform user.
 *
 * Priority:
 *   1. First + Last name  (if either is set)
 *   2. Capitalised local-part of email  (e.g. "babu" from "babu@greatleap.in")
 *   3. "Unknown"
 *
 * This avoids showing a raw email address in a "name" slot, while still
 * giving a recognisable fallback for users who haven't filled in their
 * profile yet. Use `memberEmail` separately when you also want to show the
 * full address as a subtitle.
 *
 * Accepts snake_case (from the members API) or camelCase (from /api/me).
 */
export function memberDisplayName(firstNameOrObj, lastName, email) {
  // Accept a single member/user object as the first argument for convenience.
  let first = firstNameOrObj;
  let last  = lastName;
  let mail  = email;
  if (firstNameOrObj && typeof firstNameOrObj === 'object') {
    const o = firstNameOrObj;
    first = o.first_name ?? o.firstName ?? '';
    last  = o.last_name  ?? o.lastName  ?? '';
    mail  = o.email ?? '';
  }
  const name = [first, last].filter(Boolean).join(' ').trim();
  if (name) return name;
  if (mail) {
    const local = String(mail).split('@')[0];
    return local.charAt(0).toUpperCase() + local.slice(1);
  }
  return 'Unknown';
}

const ROLE_LABELS = {
  super_admin: 'Owner',
  admin: 'Admin',
  member: 'Member',
};
export function accessRoleLabel(role) {
  return ROLE_LABELS[role] || role;
}

/** Generate 1–2 letter initials from a display name string. */
export function initials(name) {
  const parts = (name || '').split(' ').filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  return ((parts[0] || '?')[0]).toUpperCase();
}
